import type React from "react";
import { Children, isValidElement, type FC } from "react";
import {
    SelectProvider as AriakitSelectProvider,
    Select as AriakitSelect,
    SelectPopover as AriakitSelectPopover,
    SelectItem as AriakitSelectItem,
    SelectItemCheck as AriakitSelectItemCheck,
    SelectArrow as AriakitSelectArrow,
    useSelectContext,
} from "@ariakit/react";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { IconChevronDownSmall } from "central-icons/IconChevronDownSmall";
import { IconCheckmark2Small } from "central-icons/IconCheckmark2Small";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { Text } from "@/components/podkit/typography/Text";
import type { PropsWithTestId } from "@/components/podkit/lib/testid";

/**
 * Component displayName constants for Fast Refresh compatibility.
 */
const SELECT_ITEM = "Select.Item";
const SELECT_ITEM_ICON = "Select.Item.Icon";
const SELECT_ITEM_DESCRIPTION = "Select.Item.Description";
const SELECT_FOOTER = "Select.Footer";

/**
 * Props for the root Select component.
 */
export type SelectProps = PropsWithClassName &
    PropsWithTestId & {
        /** The default value for uncontrolled usage */
        defaultValue?: string;
        /** The current value for controlled usage */
        value?: string;
        /** Callback when value changes */
        onValueChange?: (value: string) => void;
        /** Whether the select is disabled */
        disabled?: boolean;
        /** Whether the select is in loading state */
        loading?: boolean;
        /** Placeholder text when no value is selected */
        placeholder?: string | React.ReactNode;
        /** Custom function to format the display value in the trigger */
        formatDisplayValue?: (value: string, label: React.ReactNode) => React.ReactNode;
        /** Whether the popover should match the trigger width. Defaults to true. */
        sameWidth?: boolean;
    };

/**
 * Props for Select.Item component.
 */
export type SelectItemProps = React.ComponentProps<typeof AriakitSelectItem> & {
    /** The value of this item */
    value: string;
};

/**
 * Helper function to get the displayName from a React element.
 */
const getComponentDisplayName = (element: React.ReactElement): string | undefined => {
    const type = element.type as React.ComponentType;
    return type.displayName;
};

/**
 * Styled placeholder text with muted color.
 */
const SelectPlaceholder: FC<React.ComponentProps<"span">> = ({ children, className, ...props }) => (
    <span className={cn("text-content-muted", className)} {...props}>
        {children}
    </span>
);
SelectPlaceholder.displayName = "Select.Placeholder";

/**
 * Footer slot for Select. Renders content at the bottom of the dropdown.
 * Uses negative margins to extend border to full width despite popover padding.
 */
const SelectFooter: FC<React.ComponentProps<"div">> = ({ children, className, ...props }) => (
    <div className={cn("-mx-1 mt-1 border-t border-border-base px-1 pt-1", className)} {...props}>
        {children}
    </div>
);
SelectFooter.displayName = SELECT_FOOTER;

/**
 * Icon slot for Select.Item. Renders an icon at the start of the item.
 */
const SelectItemIcon: FC<React.ComponentProps<"span">> = ({ children, className, ...props }) => {
    if (!children) return null;
    return (
        <span className={cn("flex-none", className)} {...props}>
            {children}
        </span>
    );
};
SelectItemIcon.displayName = SELECT_ITEM_ICON;

/**
 * Description slot for Select.Item. Renders secondary text below the main content.
 */
const SelectItemDescription: FC<React.ComponentProps<typeof Text>> = ({ children, className, ...props }) => {
    return (
        <Text className={cn("text-sm text-content-secondary", className)} {...props}>
            {children}
        </Text>
    );
};
SelectItemDescription.displayName = SELECT_ITEM_DESCRIPTION;

/**
 * Inner component that has access to the select context.
 */
type SelectTriggerProps = {
    items: React.ReactElement[];
    loading: boolean;
    disabled: boolean;
    placeholder: string | React.ReactNode;
    formatDisplayValue?: (value: string, label: React.ReactNode) => React.ReactNode;
    "data-testid"?: string;
    className?: string;
};

/**
 * Extracts only the label text from item children, excluding Icon and Description slots.
 */
const extractItemLabel = (children: React.ReactNode): React.ReactNode => {
    const labelParts: React.ReactNode[] = [];
    Children.forEach(children, (child) => {
        if (isValidElement(child)) {
            const displayName = getComponentDisplayName(child);
            // Skip Icon and Description slots
            if (displayName === SELECT_ITEM_ICON || displayName === SELECT_ITEM_DESCRIPTION) {
                return;
            }
        }
        labelParts.push(child);
    });
    return labelParts.length === 1 ? labelParts[0] : labelParts;
};

const SelectTrigger: FC<SelectTriggerProps> = ({
    items,
    loading,
    disabled,
    placeholder,
    formatDisplayValue,
    "data-testid": dataTestId,
    className,
}) => {
    const select = useSelectContext();
    const storeValue = select?.useState("value");
    // Our Select only supports single selection, so value is always string
    const currentValue = Array.isArray(storeValue) ? storeValue[0] : storeValue;

    // Find the selected item to display its label (excluding Icon and Description)
    const selectedItem = items.find((item) => item.props.value === currentValue);
    const hasValue = !!currentValue && !!selectedItem;
    const itemLabel = selectedItem
        ? extractItemLabel(selectedItem.props.children as React.ReactNode)
        : placeholder;
    const displayValue = formatDisplayValue && currentValue ? formatDisplayValue(currentValue, itemLabel) : itemLabel;

    // Render placeholder with muted styling
    const renderedPlaceholder =
        typeof placeholder === "string" ? <SelectPlaceholder>{placeholder}</SelectPlaceholder> : placeholder;

    return (
        <AriakitSelect
            data-testid={dataTestId}
            disabled={disabled || loading}
            className={cn(
                "flex h-9 items-center justify-between gap-2",
                "rounded-lg border border-border-input-default bg-surface-input px-3",
                "text-base text-content-primary",
                "transition-all duration-150 ease-out",
                "outline-none",
                // Focus state (tab navigation): brand ring
                "focus:border-border-input-active focus:ring-1 focus:ring-border-brand focus:ring-offset-1",
                // Expanded state (clicked/open): input active state
                "aria-expanded:border-border-input-active aria-expanded:ring-4 aria-expanded:ring-ring-default aria-expanded:ring-offset-0",
                "disabled:cursor-not-allowed disabled:opacity-50",
                className,
            )}
        >
            <span className="truncate">{hasValue ? displayValue : renderedPlaceholder}</span>
            {loading ? (
                <IconSpinner className="animate-spin text-content-primary" size="sm" />
            ) : (
                <AriakitSelectArrow style={{ width: 20, height: 20 }} render={<IconChevronDownSmall size={20} />} />
            )}
        </AriakitSelect>
    );
};

/**
 * A simple select dropdown component built on Ariakit.
 * Compatible with Combobox for proper state coordination.
 *
 * @example
 * ```tsx
 * <Select value={value} onValueChange={setValue}>
 *   <Select.Item value="option1">Option 1</Select.Item>
 *   <Select.Item value="option2">Option 2</Select.Item>
 * </Select>
 * ```
 *
 * @example With icon and description
 * ```tsx
 * <Select value={value} onValueChange={setValue}>
 *   <Select.Item value="folder">
 *     <Select.Item.Icon><IconFolder size={16} /></Select.Item.Icon>
 *     Documents
 *     <Select.Item.Description>Personal files</Select.Item.Description>
 *   </Select.Item>
 * </Select>
 * ```
 */
const Select: FC<React.PropsWithChildren<SelectProps>> & SelectSlots = ({
    children,
    defaultValue,
    value,
    onValueChange,
    disabled = false,
    loading = false,
    placeholder = "Select...",
    formatDisplayValue,
    sameWidth = false,
    "data-testid": dataTestId,
    className,
}) => {
    // Extract items and footer from children
    const items: React.ReactElement[] = [];
    let footer: React.ReactElement | null = null;
    Children.forEach(children, (child) => {
        if (isValidElement(child)) {
            const displayName = getComponentDisplayName(child);
            if (displayName === SELECT_ITEM) {
                items.push(child);
            } else if (displayName === SELECT_FOOTER) {
                footer = child;
            }
        }
    });

    return (
        <AriakitSelectProvider defaultValue={defaultValue} value={value} setValue={onValueChange}>
            <SelectTrigger
                items={items}
                loading={loading}
                disabled={disabled}
                placeholder={placeholder}
                formatDisplayValue={formatDisplayValue}
                data-testid={dataTestId}
                className={className}
            />
            <AriakitSelectPopover
                gutter={4}
                className={cn(
                    // z-index ensures popover appears above page content (matches Combobox)
                    "relative z-[999]",
                    sameWidth ? "w-[--popover-anchor-width]" : "min-w-[--popover-anchor-width]",
                    "max-h-96 overflow-auto",
                    "rounded-lg border border-border-base bg-surface-popover shadow",
                    "p-1",
                    // Smooth Tailwind UI-style transitions
                    "origin-top transition-all duration-200 ease-out",
                    "data-[open]:scale-100 data-[open]:opacity-100",
                    "data-[closed]:scale-95 data-[closed]:opacity-0",
                )}
                sameWidth={sameWidth}
                modal={false}
            >
                {items}
                {footer}
            </AriakitSelectPopover>
        </AriakitSelectProvider>
    );
};

/**
 * Individual item in the select dropdown.
 */
const SelectItem: FC<SelectItemProps> & SelectItemSlots = ({ children, value, className, ...props }) => {
    // Parse children to find slots
    let icon: React.ReactNode | null = null;
    let description: React.ReactNode | null = null;
    const otherChildren: React.ReactNode[] = [];

    Children.forEach(children, (child) => {
        if (isValidElement(child)) {
            const displayName = getComponentDisplayName(child);
            switch (displayName) {
                case SELECT_ITEM_ICON:
                    icon = child;
                    break;
                case SELECT_ITEM_DESCRIPTION:
                    description = child;
                    break;
                default:
                    otherChildren.push(child);
            }
        } else {
            otherChildren.push(child);
        }
    });

    // Build content based on which slots are present
    const hasSlots = !!(icon || description);
    let content: React.ReactNode;
    if (hasSlots) {
        content = (
            <div className="flex min-w-0 items-center gap-2">
                {icon}
                <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate">{otherChildren}</span>
                    {description}
                </div>
            </div>
        );
    } else {
        content = children;
    }

    return (
        <AriakitSelectItem
            value={value}
            className={cn(
                "flex w-full cursor-pointer select-none items-center justify-between gap-2",
                "rounded px-2 py-1.5",
                "text-base text-content-primary",
                "hover:bg-surface-hover",
                "data-[active-item]:bg-surface-hover",
                "focus:outline-none",
                className,
            )}
            {...props}
        >
            <div className="min-w-0 flex-1">{content}</div>
            <AriakitSelectItemCheck className="flex-none">
                <IconCheckmark2Small className="size-4" />
            </AriakitSelectItemCheck>
        </AriakitSelectItem>
    );
};
SelectItem.displayName = SELECT_ITEM;
SelectItem.Icon = SelectItemIcon;
SelectItem.Description = SelectItemDescription;

type SelectItemSlots = {
    Icon: typeof SelectItemIcon;
    Description: typeof SelectItemDescription;
};

type SelectSlots = {
    Item: typeof SelectItem;
    Placeholder: typeof SelectPlaceholder;
    Footer: typeof SelectFooter;
};

Select.Item = SelectItem;
Select.Placeholder = SelectPlaceholder;
Select.Footer = SelectFooter;

export { Select };
